import { computed, ref } from 'vue'
import usePostApi from '@/composables/post/postApi'
import usePostList from '@/composables/post/postList'
import useAppState from '@/composables/ui/ui'
import usePostListMenu, { ActivePostPage } from '@/composables/ui/postListMenu'
import { Post } from '@/model/data-object/Post'
import useUiFactory from '@/composables/ui/uiFactory'
import { ORDER } from '@/composables/api'
import useConfiguration from '@/composables/configuration/configuration'
import useReadManager from '@/composables/read/readManager'
import useAdvertManager from '@/composables/advert/advertManager'
import useContextChangeVars from '@/composables/contextCahngeVars/contextChangeVars'
import usePostStatsManager from '@/composables/post/postStatsManager'

const totalCount = ref(0)
const {
  postList,
  postTree,
  addToList,
  getLastId,
  getFirstId,
  clearListData: clearList,
  prependToList,
  prepareVirtualRoot,
  addPostToTree,
  resolveFullyLoaded,
} = usePostList()

const { configuration } = useConfiguration()

const { loadingList } = useAppState()
const changingList = ref(false)
const loadingBefore = ref(false)
const hasNext = ref(true)
const hasBefore = ref(false)
let bestPageSize = 0

export default function usePostsLoader() {
  const { activePage, isOldest: isOldestPage } = usePostListMenu()
  const { showLastSavedPost } = useAppState()
  const { contextChange } = useContextChangeVars()
  const { updateReadDate } = useReadManager()
  const { initAdverts } = useAdvertManager()
  const { loadPostsStats } = usePostStatsManager()

  const {
    fetchOldest: fetchOldestApi,
    fetchNewest: fetchNewestApi,
    fetchBest: fetchBestApi,
    fetchTopLevelNewest: fetchTopLevelNewestApi,
  } = usePostApi()

  const loadedCount = computed(() => postList.length)
  const remaining = computed(() =>
    totalCount.value > postList.length ? totalCount.value - postList.length : 0
  )

  const getPageSize = () => {
    return activePage.value === ActivePostPage.Best
      ? bestPageSize
      : configuration.pageSize
  }

  const checkDuplicate = () => {
    return activePage.value === ActivePostPage.Best
  }

  const fetchOldest = () => {
    return fetchOldestApi(ORDER.ASC, getLastId(), undefined, getPageSize())
  }

  const fetchNewest = () => {
    return fetchNewestApi(ORDER.ASC, getLastId(), undefined, getPageSize())
  }

  const fetchSingle = async (postId: number): Promise<Post[]> => {
    const { fetchSingle } = usePostApi()
    const { response, request } = fetchSingle(postId)
    const { removeHash } = useUiFactory()
    await request()

    if (response.value !== null) {
      const { configuration } = useConfiguration()

      if (response.value.topic.id !== configuration.widget.topicId) {
        removeHash()

        return []
      }

      return [response.value as Post]
    }
    removeHash()

    return []
  }

  const fetchOldestPosts = async (
    order: ORDER,
    lastId?: number
  ): Promise<Post[]> => {
    const { request, response, count } = fetchOldestApi(
      order,
      lastId,
      undefined,
      getPageSize()
    )

    await request()
    totalCount.value = count.value

    if (response.value) {
      if (order === ORDER.DESC) {
        response.value.reverse()
      }

      return response.value as Post[]
    }

    return []
  }

  const fetchBest = () => {
    bestPageSize += configuration.bestPageSize

    if (bestPageSize >= configuration.maxBestPageSize) {
      hasNext.value = false
    }

    return fetchBestApi(getPageSize())
  }

  const fetchTopNewest = () => {
    return fetchTopLevelNewestApi(configuration.underArticlePageSize)
  }

  const fetchNext = () => {
    if (activePage.value === ActivePostPage.Newest) {
      return fetchNewest()
    }
    if (activePage.value === ActivePostPage.Best) {
      return fetchBest()
    }
    if (activePage.value === ActivePostPage.TOP_NEWEST) {
      return fetchTopNewest()
    }
    return fetchOldest()
  }

  const loadNext = async () => {
    const { response, request, count } = fetchNext()
    contextChange()

    loadingList.value = true
    await request()
    if (response.value) {
      addToList(response.value as Post[], checkDuplicate())
      totalCount.value = count.value
      if (response.value.length < getPageSize()) {
        hasNext.value = false
      }
    }

    loadingList.value = false
    showLastSavedPost.value = false
    updateReadDate()
    initAdverts()
    loadPostsStats()
    resolveFullyLoaded(remaining.value)
  }

  const fetchOldestPostsDesc = async (lastId?: number): Promise<Post[]> => {
    const posts = await fetchOldestPosts(ORDER.DESC, lastId)

    if (posts.length < getPageSize()) {
      hasBefore.value = false

      return posts
    }
    hasBefore.value = true

    return posts
  }

  const fetchOldestPostsAsc = async (lastId?: number): Promise<Post[]> => {
    const posts = await fetchOldestPosts(ORDER.ASC, lastId)

    if (posts.length < getPageSize()) {
      hasNext.value = false

      return posts
    }
    hasNext.value = true

    return posts
  }

  const loadBefore = async () => {
    loadingBefore.value = true
    contextChange()
    const posts = await fetchOldestPostsDesc(getFirstId())

    prependToList(posts)
    showLastSavedPost.value = false
    updateReadDate()
    initAdverts()
    loadPostsStats()
    resolveFullyLoaded(remaining.value)

    loadingBefore.value = false
  }

  const showPost = async (postId: number) => {
    loadingList.value = true

    Promise.all([
      fetchOldestPostsDesc(postId),
      fetchSingle(postId),
      fetchOldestPostsAsc(postId),
    ]).then(async values => {
      let allPosts: Post[] = []

      values.forEach((posts: Post[]) => {
        allPosts = allPosts.concat(posts)
      })

      if (allPosts.length > 0) {
        prepareVirtualRoot(allPosts[0])
        addToList(allPosts)
      }

      loadingList.value = false
      updateReadDate()
      initAdverts()
      loadPostsStats()

      resolveFullyLoaded(remaining.value)
    })
  }

  function addPost(post: Post) {
    if (!post.parent && isOldestPage.value) {
      showLastSavedPost.value = true

      return
    }
    addPostToTree(post)
  }

  function clearListData() {
    totalCount.value = 0
    hasNext.value = true
    hasBefore.value = false
    bestPageSize = 0
    clearList()
  }

  return {
    totalCount,
    loadedCount,
    changingList,
    postList,
    postTree,
    remaining,
    clearListData,
    loadNext,
    showPost,
    addPost,
    loadBefore,
    hasBefore,
    hasNext,
    loadingBefore,
  }
}
